import { useEffect, useRef } from 'react';

interface RadarChartProps {
  data: {
    macroVsMicro: number;
    valueVsGrowth: number;
    timeHorizon: number;
    passiveVsActive: number;
    strategicVsFinancial: number;
  };
  color?: string;
  showLabels?: boolean;
  size?: number;
}

const traitLabels = [
  'Macro vs Micro',
  'Value vs Growth',
  'Time Horizon',
  'Passive vs Active',
  'Strategic vs Financial',
];

export function RadarChart({
  data,
  color = '#3b82f6',
  showLabels = true,
  size = 300,
}: RadarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 60;
    const values = Object.values(data);
    const numPoints = values.length;

    // Draw background circles
    ctx.strokeStyle = getComputedStyle(document.documentElement)
      .getPropertyValue('--tw-text-opacity')
      .includes('dark')
      ? 'rgba(148, 163, 184, 0.2)'
      : 'rgba(203, 213, 225, 0.5)';
    ctx.lineWidth = 1;

    for (let i = 1; i <= 5; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (radius * i) / 5, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = getComputedStyle(document.documentElement)
      .getPropertyValue('--tw-text-opacity')
      .includes('dark')
      ? 'rgba(148, 163, 184, 0.3)'
      : 'rgba(203, 213, 225, 0.7)';
    for (let i = 0; i < numPoints; i++) {
      const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.stroke();
    }

    // Draw data polygon
    ctx.fillStyle = color + '30';
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();

    for (let i = 0; i < numPoints; i++) {
      const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
      const value = values[i] / 100;
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }

    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw points
    ctx.fillStyle = color;
    for (let i = 0; i < numPoints; i++) {
      const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
      const value = values[i] / 100;
      const x = centerX + radius * value * Math.cos(angle);
      const y = centerY + radius * value * Math.sin(angle);

      ctx.beginPath();
      ctx.arc(x, y, 5, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Draw labels
    if (showLabels) {
      ctx.fillStyle = getComputedStyle(document.documentElement)
        .getPropertyValue('color');
      ctx.font = '12px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (let i = 0; i < numPoints; i++) {
        const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
        const labelRadius = radius + 40;
        const x = centerX + labelRadius * Math.cos(angle);
        const y = centerY + labelRadius * Math.sin(angle);

        const words = traitLabels[i].split(' ');
        words.forEach((word, idx) => {
          ctx.fillText(word, x, y + idx * 14 - ((words.length - 1) * 14) / 2);
        });
      }
    }
  }, [data, color, showLabels]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className="mx-auto"
    />
  );
}

